import * as React from "react";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import { blueGrey } from "@mui/material/colors";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import ImageListItem from "@mui/material/ImageListItem";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  createTheme,
  responsiveFontSizes,
  ThemeProvider,
} from "@mui/material/styles";

import ImageGalleryList from "./components/ImageGalleryList";
import Abstract from "./components/Abstract";
import Approach from "./components/Approach";
import ImageAttributions from "./components/ImageAttributions";
import HoverImage from "./components/HoverImage";
import ClickAlert from "./components/ClickAlert";

const images = [
  "img1.jpg",
  "img5.jpg",
  "img3.jpg",
  "img7.jpg",
  "img8.jpg",
  "img2.jpg",
  "img4.jpg",
  "img6.jpg",
];

const font_theme = responsiveFontSizes(
  createTheme({
    typography: {
      allVariants: {
        fontFamily: ["Google Sans", "sans-serif", "Roboto"].join(","),
      },
    },
  }),
);

const mainTheme = {
  headerColor: blueGrey.A700,
  headerButtonColor: "white_button",
  headerTextColor: "white",
  firstColor: "#F3F6F9",
  secondColor: "#FFFFFF",
};

export default function App() {
  const [open, setOpen] = React.useState(false);

  const queryTheme = useTheme();
  const isSmall = useMediaQuery(queryTheme.breakpoints.down("sm"));

  // const handleClickOpenAtt = () => {
  //   setOpen(true);
  // };

  const handleCloseAtt = (value) => {
    setOpen(false);
  };

  const nameSpacing = isSmall ? "   " : "        ";

  return (
    <React.Fragment>
      <div
        style={{
          width: "100%",
          zIndex: 1,
          position: null,
        }}
      >
        <Box
          style={{
            backgroundColor: mainTheme.headerColor,
            display: "flex",
            flexDirection: "column",
            color: mainTheme.headerTextColor,
            textAlign: "center",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Container maxWidth="lg">
            <ThemeProvider theme={font_theme}>
              <Typography
                variant={isSmall ? "h4" : "h3"}
                sx={{
                  my: 4,
                  mt: 8,
                  /*color: '#363636',*/ fontWeight: 500,
                  lineHeight: 1.125,
                }}
              >
                Shape2Animal: Creative Animal Generation from Natural
                Silhouettes
              </Typography>
            </ThemeProvider>

            <Box style={{ marginBottom: 0, width: "100%" }}>
              <ThemeProvider theme={font_theme}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  <p
                    style={{
                      whiteSpace: "pre-wrap",
                      lineHeight: 2,
                      fontWeight: 400,
                      fontSize: isSmall ? 14 : null,
                    }}
                  >
                    Quoc-Duy&nbsp;Tran<sup>1</sup>
                    {nameSpacing}
                    Anh-Tuan&nbsp;Vo<sup>1</sup>
                    {nameSpacing}
                    Dinh-Khoi&nbsp;Vo<sup>1</sup>
                    {nameSpacing}
                    Tam&nbsp;Nguyen<sup>2</sup>
                    {nameSpacing}
                    Minh-Triet&nbsp;Tran<sup>1</sup>
                    {nameSpacing}
                    Trung-Nghia&nbsp;Le<sup>1</sup>
                  </p>
                  <p
                    style={{
                      whiteSpace: "pre-wrap",
                      lineHeight: 2,
                      fontWeight: isSmall ? 400 : 600,
                    }}
                  >
                    <sup>1</sup>University&nbsp;of&nbsp;Science, VNU-HCM
                    {isSmall ? <br /> : "          "}
                    <sup>2</sup>University&nbsp;of&nbsp;Dayton
                  </p>

                  {/* <p style={{ whiteSpace: 'pre-wrap', lineHeight: 2, fontWeight: isSmall ? 400 : 600 }}>PROCEEDINGS OF THE IEEE</p> */}
                </Typography>
              </ThemeProvider>
              <Stack
                direction="row"
                spacing={2}
                justifyContent="center"
                sx={{ mt: 1, mb: 2 }}
              >
                <Button
                  href="https://arxiv.org/abs/2506.20616"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  size="large"
                  color={mainTheme.headerButtonColor}
                  sx={{ width: 120, textTransform: "none" }}
                >
                  {" "}
                  arXiv{" "}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  color={mainTheme.headerButtonColor}
                  sx={{ width: 200, textTransform: "none" }}
                >
                  {" "}
                  Code (coming soon){" "}
                </Button>
              </Stack>
            </Box>
          </Container>
          <Container maxWidth="md" style={{ marginBottom: 24 }}>
            <video
              poster=""
              autoPlay
              controls
              muted
              loop
              width="100%"
              style={{ borderRadius: "12px" }}
            >
              <source src="teaser.mp4" type="video/mp4" />
            </video>
          </Container>
        </Box>

        <Box
          sx={{
            backgroundColor: mainTheme.firstColor,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Abstract />
        </Box>

        <Box
          sx={{
            backgroundColor: mainTheme.secondColor,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          <Container
            maxWidth="lg"
            sx={{ margin: 4 }}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Container maxWidth="md" sx={{ margin: 4, textAlign: "justify" }}>
              <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                ANIMAL TRANSFORMATION
              </Typography>
              <Typography>
                Our Shape2Animal model effectively transforms natural elements
                like clouds, stones, and fire into visually coherent animal
                forms while preserving the original silhouette and background.
                Despite being trained on silhouette interpretation techniques,
                the model demonstrates remarkable generalization to diverse
                natural shapes, seamlessly converting these everyday elements
                into realistic animal representations that maintain spatial
                consistency with the original scene.
              </Typography>
              <ClickAlert />
            </Container>
            <ImageGalleryList>
              {images.map((item) => (
                <ImageListItem key={item}>
                  <HoverImage
                    srcTop={`/images/inputs/${item}`}
                    srcHover={`/images/masks/${item}`}
                    srcClick={`/images/results/${item}`}
                  />
                </ImageListItem>
              ))}
            </ImageGalleryList>
          </Container>
        </Box>

        <Box
          sx={{
            backgroundColor: mainTheme.firstColor,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Approach />
        </Box>

        <Box
          maxWidth="100%"
          sx={{
            backgroundColor: mainTheme.secondColor,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Container
            sx={{
              maxWidth: "100%",
              padding: 2,
              backgroundColor: "#f5f5f5",
              fontFamily: "monospace",
              whiteSpace: "pre",
              overflowX: "auto",
              overflowY: "hidden",
            }}
          >
            {String.raw`@article{Tran2025Shape2Animal,
  title = {Shape2Animal: Creative Animal Generation from Natural Silhouettes},
  author = {Tran, Quoc-Duy and Vo, Anh-Tuan and Vo, Dinh-Khoi and Nguyen, Tam V. and Tran, Minh-Triet and Le, Trung-Nghia},
  journal = {IEEE MultiMedia},
  year = {2026}
}`}
          </Container>
        </Box>

        <Box
          sx={{
            backgroundColor: blueGrey.A100,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Container maxWidth="md" sx={{ margin: 4 }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={4}>
              <Stack>
                <Typography
                  variant="h6"
                  component="h2"
                  sx={{
                    fontWeight: 600,
                    textAlign: { xs: "left", md: "right" },
                  }}
                >
                  ACKNOWLEDGMENT
                </Typography>
              </Stack>
              <Stack>
                <Typography
                  variant="body1"
                  component="span"
                  sx={{ textAlign: "left" }}
                >
                  This page was built using the template adopted from the{" "}
                  <Link
                    href="https://objectdrop.github.io"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    ObjectDrop
                  </Link>{" "}
                  project page.
                  {/* <p>We thank owners of images on this site { (<Link onClick={handleClickOpenAtt}>link</Link> for attributions)} ... for sharing their valuable assets.</p> */}
                </Typography>
                <ImageAttributions open={open} onClose={handleCloseAtt} />
              </Stack>
            </Stack>
          </Container>
        </Box>
      </div>
    </React.Fragment>
  );
}
